import { MetaRating } from "@/api/privateRequests/squad-builder/get-meta-ratings";

export type Chemistry = 0 | 1 | 2 | 3;

export interface StateChemstyle {
  currentChemstyleId: number;
  temporaryChemstyleId: number;
  bestChemStyleId: number;
  hasSelectedChemStyleManually: boolean;
}

export interface StateChemistry {
  currentChemistry: Chemistry;
  temporaryChemistry: Chemistry;
}
export interface StateMetaRating {
  currentMetaRating: number;
  temporaryMetaRating: number;
  metaRatingZeroChem: number;
}

type DetailedPlayerState = {
  chemistry: StateChemistry;
  chemStyle: StateChemstyle;
  playerRoleId: number;
  metaRating: StateMetaRating;
  metaRatingsForCurrentRole: MetaRating[];
  metaRatingsForAllRoles: MetaRating[];
};

const getMetaRating = ({
  chemistry,
  chemStyleId,
  metaRatingsForCurrentRole,
}: {
  chemistry: Chemistry;
  chemStyleId: number;
  metaRatingsForCurrentRole: MetaRating[];
}): MetaRating => {
  if (chemistry === 0) {
    return metaRatingsForCurrentRole.find(
      (metaRating) => metaRating.chemistry === 0,
    );
  }
  return metaRatingsForCurrentRole.find(
    (metaRating) =>
      metaRating.chemistry === chemistry &&
      metaRating.chemstyleId === chemStyleId,
  );
};

const getBestMetaRating = ({
  chemistry,
  metaRatingsForCurrentRole,
}: {
  chemistry: Chemistry;
  metaRatingsForCurrentRole: MetaRating[];
}): MetaRating | null => {
  const bestChemStyle = metaRatingsForCurrentRole.find(
    (metaRating) =>
      metaRating.isBestChemstyleAtChem && metaRating.chemistry === chemistry,
  );
  return bestChemStyle;
};

export const detailedPlayerReducer = (
  state: DetailedPlayerState,
  action: DetailedPlayerAction,
): DetailedPlayerState => {
  let metaRating: MetaRating;
  let bestMetaRating: MetaRating;
  let bestChemStyleMetaRating: MetaRating;
  let chemistry: Chemistry;
  let metaRatingZeroChem: MetaRating;
  switch (action.type) {
    case "SET_CHEMISTRY":
      metaRating = getMetaRating({
        chemistry: action.payload,
        chemStyleId: state.chemStyle.currentChemstyleId,
        metaRatingsForCurrentRole: state.metaRatingsForCurrentRole,
      });
      bestMetaRating = getBestMetaRating({
        chemistry: action.payload === 0 ? 3 : action.payload,
        metaRatingsForCurrentRole: state.metaRatingsForCurrentRole,
      });

      return {
        ...state,
        chemistry: {
          ...state.chemistry,
          currentChemistry: action.payload,
        },
        metaRating: {
          ...state.metaRating,
          currentMetaRating: metaRating?.metaRating,
        },
        chemStyle: {
          ...state.chemStyle,
          ...(state.chemistry.currentChemistry === 0 && {
            currentChemstyleId: bestMetaRating?.chemstyleId,
          }),
          bestChemStyleId: bestMetaRating?.chemstyleId,
        },
      };
    case "SET_TEMPORARY_CHEMISTRY":
      if (action.payload !== undefined) {
        metaRating = getMetaRating({
          chemistry: action.payload,
          chemStyleId: state.chemStyle.currentChemstyleId,
          metaRatingsForCurrentRole: state.metaRatingsForCurrentRole,
        });
        bestMetaRating = getBestMetaRating({
          chemistry: action.payload === 0 ? 3 : action.payload,
          metaRatingsForCurrentRole: state.metaRatingsForCurrentRole,
        });
      }
      return {
        ...state,
        chemistry: {
          ...state.chemistry,
          temporaryChemistry: action.payload,
        },
        metaRating: {
          ...state.metaRating,
          temporaryMetaRating: metaRating?.metaRating,
        },
        chemStyle: {
          ...state.chemStyle,
          ...(state.chemistry.currentChemistry === 0 && {
            temporaryChemstyleId: bestMetaRating?.chemstyleId,
          }),
        },
      };

    case "SET_CHEMSTYLE":
      chemistry =
        state.chemistry.currentChemistry === 0
          ? 3
          : state.chemistry.currentChemistry;
      metaRating = getMetaRating({
        chemistry,
        chemStyleId: action.payload,
        metaRatingsForCurrentRole: state.metaRatingsForCurrentRole,
      });

      return {
        ...state,
        chemStyle: {
          ...state.chemStyle,
          currentChemstyleId: action.payload,
        },
        chemistry: {
          ...state.chemistry,
          currentChemistry: chemistry,
        },
        metaRating: {
          ...state.metaRating,
          currentMetaRating: metaRating?.metaRating,
        },
      };
    case "SET_TEMPORARY_CHEMSTYLE":
      if (action.payload) {
        metaRating = getMetaRating({
          chemistry:
            state.chemistry.currentChemistry === 0
              ? 3
              : state.chemistry.currentChemistry,
          chemStyleId: action.payload,
          metaRatingsForCurrentRole: state.metaRatingsForCurrentRole,
        });
        chemistry =
          state.chemistry.currentChemistry === 0
            ? 3
            : state.chemistry.currentChemistry;
      }
      return {
        ...state,
        chemStyle: {
          ...state.chemStyle,
          temporaryChemstyleId: action.payload,
        },
        metaRating: {
          ...state.metaRating,
          temporaryMetaRating: metaRating?.metaRating,
        },
        chemistry: {
          ...state.chemistry,
          temporaryChemistry: chemistry,
        },
      };

    case "SET_PLAYER_ROLE_ID":
      return {
        ...state,
        playerRoleId: action.payload,
      };
    case "SET_META_RATING":
      return {
        ...state,
        metaRating: { ...state.metaRating, currentMetaRating: action.payload },
      };
    case "SET_TEMPORARY_META_RATING":
      return {
        ...state,
        metaRating: {
          ...state.metaRating,
          temporaryMetaRating: action.payload,
        },
      };
    case "SET_META_RATING_ZERO_CHEM":
      return {
        ...state,
        metaRating: { ...state.metaRating, metaRatingZeroChem: action.payload },
      };
    case "SET_META_RATINGS_FOR_CURRENT_ROLE":
      const metaRatingsForCurrentRole: MetaRating[] = [];
      const metaRatingsForAllRoles: MetaRating[] = [];

      action.payload.forEach((metaRating) => {
        if (metaRating.playerRoleId === state.playerRoleId) {
          metaRatingsForCurrentRole.push(metaRating);
        }
        if (metaRating.isBestChemstyleAtChem) {
          metaRatingsForAllRoles.push(metaRating);
        }
      });

      bestMetaRating = getBestMetaRating({
        chemistry: state.chemistry.currentChemistry,
        metaRatingsForCurrentRole: metaRatingsForCurrentRole,
      });

      bestChemStyleMetaRating = getBestMetaRating({
        chemistry: 3,
        metaRatingsForCurrentRole: metaRatingsForCurrentRole,
      });

      metaRatingZeroChem = getMetaRating({
        chemistry: 0,
        chemStyleId: 250,
        metaRatingsForCurrentRole: metaRatingsForCurrentRole,
      });

      return {
        ...state,
        metaRatingsForCurrentRole: metaRatingsForCurrentRole,
        chemStyle: {
          ...state.chemStyle,
          bestChemStyleId: bestChemStyleMetaRating?.chemstyleId,
          currentChemstyleId: bestChemStyleMetaRating?.chemstyleId,
        },
        metaRating: {
          ...state.metaRating,
          currentMetaRating: bestMetaRating?.metaRating,
          metaRatingZeroChem: metaRatingZeroChem?.metaRating,
        },
        metaRatingsForAllRoles: metaRatingsForAllRoles,
      };
    default:
      return state;
  }
};

export type DetailedPlayerAction =
  | { type: "SET_CHEMISTRY"; payload: Chemistry }
  | { type: "SET_TEMPORARY_CHEMISTRY"; payload: Chemistry }
  | { type: "SET_CHEMSTYLE"; payload: number }
  | { type: "SET_TEMPORARY_CHEMSTYLE"; payload: number }
  // | { type: "SET_PLAYER_ROLE"; payload: string }
  | { type: "SET_META_RATING"; payload: number }
  | { type: "SET_TEMPORARY_META_RATING"; payload: number }
  | { type: "SET_META_RATING_ZERO_CHEM"; payload: number }
  | { type: "SET_META_RATINGS_FOR_CURRENT_ROLE"; payload: MetaRating[] }
  | { type: "SET_PLAYER_ROLE_ID"; payload: number };
